import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, Loader2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { TANPURA_KEYS } from "@/lib/notes";
import { startTanpura, stopSource, masterGain, preloadTanpura } from "@/lib/audio";

export default function Tanpura() {
    const [keyIdx, setKeyIdx] = useState(7); // default G
    const [playing, setPlaying] = useState(false);
    const [volume, setVolume] = useState(0.85);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const sourceRef = useRef(null);
    const requestIdRef = useRef(0);

    // Preload tanpura sample on first mount
    useEffect(() => {
        preloadTanpura().then(() => setReady(true)).catch(() => setReady(true));
    }, []);

    // Apply master volume on change
    useEffect(() => {
        const m = masterGain();
        if (m) m.gain.value = volume;
    }, [volume]);

    // Start / stop / change key
    useEffect(() => {
        const reqId = ++requestIdRef.current;
        if (!playing) {
            if (sourceRef.current) {
                stopSource(sourceRef.current);
                sourceRef.current = null;
            }
            setLoading(false);
            return;
        }
        // Stop previous, then start new
        if (sourceRef.current) {
            stopSource(sourceRef.current);
            sourceRef.current = null;
        }
        setLoading(true);
        const key = TANPURA_KEYS[keyIdx];
        startTanpura(key.note, 1.0)
            .then((src) => {
                if (requestIdRef.current !== reqId) {
                    // user changed selection again
                    stopSource(src);
                    return;
                }
                sourceRef.current = src;
                setLoading(false);
            })
            .catch(() => setLoading(false));

        return () => {
            // Cleanup on unmount or before next effect run
            if (sourceRef.current) {
                stopSource(sourceRef.current);
                sourceRef.current = null;
            }
        };
    }, [playing, keyIdx]);

    return (
        <div data-testid="tanpura-page" className="space-y-10">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Tool · 01
                </div>
                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
                    Tanpura
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    A real recorded tanpura drone (Sa–Pa–Sa–Sa cycle), pitch-shifted live to any of the 12 keys. Headphones recommended.
                </p>
            </header>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Key selector */}
                <div className="lg:col-span-2 rounded-lg border border-border bg-card p-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="font-bold text-2xl">Tonic (Sa)</div>
                        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                            12 keys
                        </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                        {TANPURA_KEYS.map((k, i) => {
                            const active = i === keyIdx;
                            return (
                                <button
                                    key={k.note}
                                    data-testid={`tanpura-key-${k.note}`}
                                    onClick={() => setKeyIdx(i)}
                                    className={`group relative h-20 rounded-md border text-left px-3 py-2 transition-all ${
                                        active
                                            ? "bg-primary text-primary-foreground border-primary shadow-[inset_0_0_0_1px_hsl(15_54%_60%)]"
                                            : "bg-secondary/40 border-border hover:border-primary/50 hover:-translate-y-0.5"
                                    }`}
                                >
                                    <div className="font-bold text-2xl leading-none">
                                        {k.note}
                                    </div>
                                    <div className={`font-mono text-[10px] mt-2 ${active ? "opacity-90" : "text-muted-foreground"}`}>
                                        {k.freq.toFixed(2)} Hz
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Transport */}
                <div className="rounded-lg border border-border bg-card p-8 flex flex-col gap-7">
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
                            Now playing
                        </div>
                        <div className="font-bold text-5xl">
                            {TANPURA_KEYS[keyIdx].note}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground mt-1">
                            {TANPURA_KEYS[keyIdx].label}
                        </div>
                    </div>

                    <button
                        data-testid="tanpura-play-button"
                        onClick={() => setPlaying((p) => !p)}
                        disabled={loading && !ready}
                        className={`h-14 rounded-md font-medium text-base flex items-center justify-center gap-2 transition-all ${
                            playing
                                ? "bg-foreground text-background hover:opacity-90"
                                : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                        } ${loading ? "opacity-90" : ""}`}
                    >
                        {loading ? (
                            <>
                                <Loader2 size={18} className="animate-spin" />
                                Loading sample…
                            </>
                        ) : playing ? (
                            <>
                                <Pause size={18} /> Stop
                            </>
                        ) : (
                            <>
                                <Play size={18} /> Play tanpura
                            </>
                        )}
                    </button>

                    {/* Volume */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
                                <Volume2 size={14} /> Volume
                            </label>
                            <span className="font-mono text-xs text-muted-foreground">
                                {Math.round(volume * 100)}%
                            </span>
                        </div>
                        <Slider
                            data-testid="tanpura-volume-slider"
                            value={[volume]}
                            onValueChange={([v]) => setVolume(v)}
                            min={0}
                            max={1}
                            step={0.01}
                        />
                    </div>

                    {/* Pluck cycle reference */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                            Pluck cycle
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {["Pa", "Sa", "Sa", "Sa̱"].map((label, i) => (
                                <div
                                    key={i}
                                    data-testid={`tanpura-step-${i}`}
                                    className={`h-12 rounded-md border flex items-center justify-center font-bold  text-lg ${
                                        playing
                                            ? "border-primary/40 text-foreground bg-primary/5"
                                            : "border-border bg-secondary/40 text-muted-foreground"
                                    }`}
                                >
                                    {label}
                                </div>
                            ))}
                        </div>
                        {playing && (
                            <div className="mt-3 flex items-center gap-2 text-xs font-mono text-primary">
                                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                                Looping live recording at {TANPURA_KEYS[keyIdx].note}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
